import React, { useState, useEffect } from 'react';
import { CardSkeleton } from '../../../components/enterprise';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  Chip,
  IconButton,
  CircularProgress,
  Divider,
  Grid,
  InputAdornment,
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  PlayArrow as TestIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Storage as StorageIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useNotification } from '../../../contexts/NotificationContext';

const API_BASE = '/api/v1.0/settings/system';

const SystemSettingsPage = () => {
  const notification = useNotification();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingClickHouse, setTestingClickHouse] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Settings state
  const [clickHouseSettings, setClickHouseSettings] = useState([]);
  const [originalSettings, setOriginalSettings] = useState({});

  // Password visibility
  const [showPasswords, setShowPasswords] = useState({});

  // Test result
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/clickhouse`);
      if (response.data?.success) {
        setClickHouseSettings(response.data.data || []);
      }

      // Store original values for change detection
      const original = {};
      (response.data?.data || []).forEach(s => {
        original[s.settingKey] = s.settingValue;
      });
      setOriginalSettings(original);
      setHasChanges(false);
    } catch (error) {
      console.error('Failed to fetch system settings:', error);
      notification.error('Failed to load system settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSettingChange = (settingKey, value) => {
    setClickHouseSettings(prev =>
      prev.map(s =>
        s.settingKey === settingKey
          ? { ...s, settingValue: value }
          : s
      )
    );
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const changedSettings = [];

      clickHouseSettings.forEach(s => {
        if (s.isSensitive && s.settingValue === '********') {
          return;
        }
        if (s.settingValue !== originalSettings[s.settingKey]) {
          changedSettings.push({
            settingKey: s.settingKey,
            settingValue: s.settingValue,
          });
        }
      });

      if (changedSettings.length === 0) {
        notification.info('No changes to save');
        setSaving(false);
        return;
      }

      const response = await axios.put(`${API_BASE}/bulk`, changedSettings);
      if (response.data?.success) {
        notification.success('Settings saved successfully');
        await fetchSettings();
      }
    } catch (error) {
      console.error('Failed to save settings:', error);
      notification.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestClickHouse = async () => {
    setTestingClickHouse(true);
    try {
      const response = await axios.post(`${API_BASE}/clickhouse/test`);
      setTestResult(response.data?.data || { success: false, message: 'Unknown error' });
      if (response.data?.data?.success) {
        notification.success('ClickHouse connection successful');
      } else {
        notification.warning(response.data?.data?.message || 'ClickHouse connection failed');
      }
    } catch (error) {
      console.error('Failed to test ClickHouse:', error);
      const errorMsg = error.response?.data?.error || error.message;
      setTestResult({ success: false, message: errorMsg });
      notification.error('ClickHouse connection test failed');
    } finally {
      setTestingClickHouse(false);
    }
  };

  const togglePasswordVisibility = (key) => {
    setShowPasswords(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const renderSettingField = (setting) => {
    const isPassword = setting.isSensitive;
    const showPassword = showPasswords[setting.settingKey];

    return (
      <Grid item xs={12} md={6} key={setting.settingKey}>
        <TextField
          fullWidth
          label={formatLabel(setting.settingKey)}
          value={setting.settingValue}
          onChange={(e) => handleSettingChange(setting.settingKey, e.target.value)}
          type={isPassword && !showPassword ? 'password' : 'text'}
          size="small"
          helperText={setting.description}
          placeholder={setting.defaultValue || ''}
          InputProps={{
            endAdornment: isPassword ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => togglePasswordVisibility(setting.settingKey)}
                >
                  {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
      </Grid>
    );
  };

  const formatLabel = (key) => {
    return key
      .split('.')
      .pop()
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const renderConnectivityStatus = (result) => {
    if (!result) return null;

    return (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
        {result.success ? (
          <Chip
            icon={<CheckCircleIcon />}
            label="Connected"
            color="success"
            size="small"
          />
        ) : (
          <Chip
            icon={<ErrorIcon />}
            label="Failed"
            color="error"
            size="small"
          />
        )}
        {result.responseTimeMs && (
          <Typography variant="caption" color="text.secondary">
            {result.responseTimeMs}ms
          </Typography>
        )}
        {result.version && (
          <Typography variant="caption" color="text.secondary">
            ({result.version})
          </Typography>
        )}
      </Stack>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
        <Box sx={{ p: 3 }}><CardSkeleton count={2} /></Box>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h6" gutterBottom>
            System Connections
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure the ClickHouse database connection used for metrics, traces, and analytics storage.
            Changes take effect immediately after saving.
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button
            startIcon={<RefreshIcon />}
            onClick={fetchSettings}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            startIcon={testingClickHouse ? <CircularProgress size={16} /> : <TestIcon />}
            onClick={handleTestClickHouse}
            disabled={testingClickHouse}
          >
            Test Connection
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={!hasChanges || saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Stack>
      </Stack>

      {hasChanges && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          You have unsaved changes. Click "Save Changes" to apply them.
        </Alert>
      )}

      {/* ClickHouse Settings */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <StorageIcon color="primary" />
                <Typography variant="h6">ClickHouse</Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Column-oriented database for metrics, traces, logs, and analytics
              </Typography>
              {renderConnectivityStatus(testResult)}
            </Box>
            <Button
              variant="outlined"
              size="small"
              startIcon={testingClickHouse ? <CircularProgress size={16} /> : <TestIcon />}
              onClick={handleTestClickHouse}
              disabled={testingClickHouse}
            >
              Test Connection
            </Button>
          </Stack>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={2}>
            {clickHouseSettings.map(setting => renderSettingField(setting))}
          </Grid>

          {testResult && !testResult.success && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {testResult.message}
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Info Box */}
      <Card>
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle2">Configuration Notes</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            • Settings are stored in the database and take precedence over application.properties values.
            <br />
            • Changes are applied immediately after saving - no server restart required.
            <br />
            • Use the "Test Connection" button to verify connectivity before saving.
            <br />
            • For production deployments, you can also use environment variables as fallback values.
            <br />
            • Sensitive values (like passwords) are masked in the UI but stored securely.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SystemSettingsPage;
